import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  CommunicationLocale,
  CreateCommunicationLocaleInput,
} from "../shared/communications-types";

export const COMMUNICATION_LOCALES_QUERY_KEY = ["communications", "locales"] as const;

async function fetchLocales(): Promise<readonly CommunicationLocale[]> {
  return httpClient<readonly CommunicationLocale[]>("/api/admin/communications/locales");
}

/** Communications locale catalog (API-COMM-1). Drives the locale selectors
 *  on the translation editor + add-translation flow. */
export function useCommunicationLocales() {
  return useQuery({
    queryKey: COMMUNICATION_LOCALES_QUERY_KEY,
    queryFn: fetchLocales,
    staleTime: 5 * 60_000,
  });
}

async function createLocale(input: CreateCommunicationLocaleInput): Promise<CommunicationLocale> {
  return httpClient<CommunicationLocale>("/api/admin/communications/locales", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useCreateCommunicationLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLocale,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: COMMUNICATION_LOCALES_QUERY_KEY,
      });
    },
  });
}
